import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import {
  createHashRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import Layout from "./Common/Layout";
import HomePage from "./Pages/HomePage/HomePage";
import AboutPage from "./Pages/AboutPage/AboutPage";
import LoginPage from "./Pages/LoginPage/LoginPage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Collapse, Dropdown, initTE } from "tw-elements";
import { useSelector } from "react-redux";
import UpdatePassword from "Pages/LoginPage/UpdatePassword";
import PaymentPortal from "Pages/PaymentPortal/PaymentPortal";
import PaymentStripeStatus from "Pages/PaymentPortal/PaymentStripeStatus";
import PaymentsPage from "Pages/PaymentsPage/PaymentsPage";
import PaymentRequestPortal from "Pages/PaymentPortal/PaymentRequestPortal";
import CreatePasswordPage from "Pages/CreatePasswordPage/CreatePasswordPage";
import TwoFactorAuthPage from "Pages/TwoFactorAuthPage/TwoFactorAuthPage";
import OtpVerificationPage from "Pages/OtpVerificationPage/OtpVerificationPage";
import SupportedBrowsers from "Pages/SupportedBrowsers/SupportedBrowsers";
import { WebSocketProvider } from "contexts/WebSocketContext";
import { JobProvider, useJob } from "contexts/JobContext";
import ShortcutPage from "Pages/ShortcutPage/ShortcutPage";
import { useVersionChecker } from "CustomHooks/useVersionChecker";
import ForgotPasswordPage from "Pages/ForgotPasswordPage/ForgotPasswordPage";
import { wrapRoutes } from "Common/ErrorBoundaries";

const PatientIntakeApp = lazy(
  () =>
    import(
      /* webpackChunkName: "patient-intake" */ "./PatientIntake/PatientIntakeApp"
    ),
);

const IntakeAdminApp = lazy(
  () =>
    import(
      /* webpackChunkName: "intake-admin" */ "./IntakeAdmin/IntakeAdminApp.jsx"
    ),
);

const intakeSuspenseFallback = (
  <div className="flex h-screen items-center justify-center bg-gray-50 text-slate-600 text-sm">
    Loading…
  </div>
);

initTE({ Dropdown });
const RequireAuth = ({ children }) => {
  const location = useLocation();
  let token = useSelector((state) => state.userAuth.userinfo.token);
  if (!token) {
    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get("patientId");
    const visitId = searchParams.get("visitId");
    if (visitId) {
      return <Navigate to={`/login/?visitId=${visitId}`} />;
    } else if (patientId) {
      return <Navigate to={`/login/?patientId=${patientId}`} />;
    }
    return <Navigate to="/login" />;
  }
  return children;
};
const router = createHashRouter(
  wrapRoutes([
    {
      path: "/",
      element: (
        <RequireAuth>
          <Layout />
        </RequireAuth>
      ),
      children: [
        {
          path: "",
          element: <HomePage />,
        },
      ],
    },
    {
      path: "forgot-password",
      element: <ForgotPasswordPage />,
    },
    {
      path: "update-password",
      element: <UpdatePassword />,
    },
    {
      path: "create-password",
      element: <CreatePasswordPage />,
    },
    {
      path: "about",
      element: <AboutPage />,
    },
    {
      path: "/login",
      element: <LoginPage />,
    },
    // {
    //   path: "/dev",
    //   element: <ExampleUsage />,
    // },
    {
      path: "/paymentgateway",
      element: <PaymentPortal />,
    },
    {
      path: "/paymentrequestgateway",
      element: <PaymentRequestPortal />,
    },
    {
      path: "/paymentgateway/:id",
      element: <PaymentPortal />,
    },
    {
      path: "/paymentgateway/stripe/:status",
      element: <PaymentStripeStatus />,
    },
    {
      path: "/payments/:id",
      element: <PaymentsPage />,
    },
    {
      path: "/two-factor-auth",
      element: <TwoFactorAuthPage />,
    },
    {
      path: "/verify-otp",
      element: <OtpVerificationPage />,
    },
    {
      path: "/s/:code",
      element: <ShortcutPage />,
    },
    {
      path: "/intake",
      element: (
        <Suspense fallback={intakeSuspenseFallback}>
          <PatientIntakeApp />
        </Suspense>
      ),
    },
    {
      path: "/intake-admin",
      element: (
        // <RequireAuth>
        <Suspense fallback={intakeSuspenseFallback}>
          <IntakeAdminApp />
        </Suspense>
        // </RequireAuth>
      ),
    },

    // {
    //   path: "/paymentsuccess",
    //   element: <PaymentSuccessPage />,
    // },
    // {
    //   path: "/paymentfailed",
    //   element: <PaymentFailedPage />,
    // },
  ]),
);

// This component now correctly uses the jobs from the context
const AppContent = () => {
  const { jobs } = useJob();
  const hasNewVersion = useVersionChecker();
  const newOrigin = "https://trurev.gettrucare.com";
  const currentUrl = window.location.href;

  const url = new URL(currentUrl);
  const rest = url.href.replace(url.origin, "");

  window.location.replace(`${newOrigin}${rest}`);
  return;
  useEffect(() => {
    initTE({ Dropdown });
    const interval = setInterval(() => {
      initTE({ Collapse });
    }, 2000);

    return () => {
      // window.removeEventListener(listener);
      clearInterval(interval);
    };
  }, []);

  const allowedBrowsers = ["chrome", "opera", "firefox", "edg"]; // 'edg' matches Edge
  const userAgent = navigator.userAgent.toLowerCase();

  // Chrome on iOS has 'crios' in the user agent
  const isChromeiOS = userAgent.includes("crios");
  const isFirefoxiOS = userAgent.includes("fxios");
  const isOperaiOS = userAgent.includes("opt");
  const isEdgeiOS = userAgent.includes("edgios");

  const isAllowed =
    allowedBrowsers.some((browser) => userAgent.includes(browser)) ||
    isChromeiOS ||
    isFirefoxiOS ||
    isOperaiOS ||
    isEdgeiOS;
  const path = window.location.hash.replace(/^#/, "");
  const allowedPaths = [
    "/paymentgateway",
    "/paymentrequestgateway",
    "/payments",
    "/s",
    "/intake",
  ].some((allowed) => path.startsWith(allowed));

  if (!isAllowed && !allowedPaths) {
    return <SupportedBrowsers />;
  }
  return (
    <WebSocketProvider jobs={jobs}>
      <>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <div>
          <RouterProvider router={router} />
        </div>
        {hasNewVersion && (
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "#ffcc00",
              color: "#000",
              padding: "10px 20px",
              borderRadius: "8px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              zIndex: 9999,
            }}
          >
            🔄 A new version is available.
            <button
              onClick={() => window.location.reload(true)}
              style={{
                marginLeft: "10px",
                background: "#000",
                color: "#fff",
                border: "none",
                padding: "5px 10px",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Refresh
            </button>
          </div>
        )}
      </>
    </WebSocketProvider>
  );
};

function App() {
  return (
    <JobProvider>
      <AppContent />
    </JobProvider>
  );
}

export default App;

